import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { Calendar, User, Plus, X, Search, CheckCircle, AlertCircle, Shield, Edit, Trash2, TrendingUp, Clock, Award } from 'lucide-react'
import './OffDuty.css'

const API_URL = import.meta.env.VITE_API_URL || 'https://www.secs.gismac.co.ke/api'

const offDayTypes = [
  { id: 'Rest Day', name: 'Rest Day', color: '#4caf50' },
  { id: 'Weekend', name: 'Weekend', color: '#2196f3' },
  { id: 'Public Holiday', name: 'Public Holiday', color: '#ff9800' },
  { id: 'Compensatory', name: 'Compensatory Off', color: '#9c27b0' },
  { id: 'Sick Off', name: 'Sick Off', color: '#f44336' },
  { id: 'Emergency Off', name: 'Emergency Off', color: '#e91e63' },
  { id: 'Training Off', name: 'Training Off', color: '#009688' }
]

const statusColors = {
  'Scheduled': '#ff9800', 'Active': '#4caf50', 'Completed': '#2196f3',
  'Cancelled': '#f44336', 'Pending Approval': '#9c27b0', 'Approved': '#4caf50', 'Pending': '#9c27b0'
}

const OffDays = () => {
  const [records, setRecords] = useState([])
  const [stats, setStats] = useState({ scheduled: 0, active: 0, completed: 0, pending: 0, cancelled: 0, total: 0 })
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [message, setMessage] = useState({ type: '', text: '' })
  const [guards, setGuards] = useState([])
  const [formData, setFormData] = useState({
    guardId: '', guardName: '', offDayType: 'Rest Day', offDate: '', endDate: '',
    reason: '', contactNumber: '', emergencyContact: '', site: '', replacementGuardId: ''
  })

  useEffect(() => { fetchRecords(); fetchStats(); fetchGuards(); }, [])

  const showMessage = (type, text) => { setMessage({ type, text }); setTimeout(() => setMessage({ type: '', text: '' }), 4000) }
  const getToken = () => localStorage.getItem('token')

  const fetchRecords = async () => {
    setLoading(true)
    try {
      const res = await axios.get(`${API_URL}/offduty`, { headers: { Authorization: `Bearer ${getToken()}` } })
      const data = res.data.data || []
      setRecords(data)
      computeLocalStats(data)
    } catch (err) { showMessage('error', 'Failed to load off days') }
    finally { setLoading(false) }
  }

  const computeLocalStats = (data) => {
    const s = { scheduled: 0, active: 0, completed: 0, pending: 0, cancelled: 0, total: data.length }
    data.forEach(r => {
      const st = r.status || 'Scheduled'
      if (st === 'Scheduled' || st === 'Approved') s.scheduled++
      else if (st === 'Active') s.active++
      else if (st === 'Completed') s.completed++
      else if (st === 'Pending Approval' || st === 'Pending') s.pending++
      else if (st === 'Cancelled') s.cancelled++
      else s.scheduled++
    })
    setStats(s)
  }

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/offduty/stats`, { headers: { Authorization: `Bearer ${getToken()}` } })
      if (res.data.success && res.data.data) setStats(res.data.data)
    } catch (err) { console.log('Stats fetch failed') }
  }

  const fetchGuards = async () => {
    try {
      const res = await axios.get(`${API_URL}/guards`, { headers: { Authorization: `Bearer ${getToken()}` } })
      setGuards(res.data.data || [])
    } catch (err) { console.log('Failed to load guards') }
  }

  const calculateDays = (start, end) => {
    if (!start || !end) return 1
    const s = new Date(start), e = new Date(end)
    s.setHours(0,0,0,0); e.setHours(0,0,0,0)
    return Math.ceil((e - s) / (1000 * 60 * 60 * 24)) + 1
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const data = { ...formData }
      if (data.offDate && data.endDate) data.daysOff = calculateDays(data.offDate, data.endDate)
      else data.daysOff = 1
      if (selectedRecord) {
        await axios.put(`${API_URL}/offduty/${selectedRecord._id}`, data, { headers: { Authorization: `Bearer ${getToken()}` } })
        showMessage('success', 'Off day updated')
      } else {
        await axios.post(`${API_URL}/offduty`, data, { headers: { Authorization: `Bearer ${getToken()}` } })
        showMessage('success', 'Off day scheduled')
      }
      setShowModal(false); setSelectedRecord(null); resetForm(); fetchRecords(); fetchStats()
    } catch (err) { showMessage('error', err.response?.data?.message || 'Failed to save') }
  }

  const handleApprove = async (id) => {
    try { await axios.put(`${API_URL}/offduty/${id}/approve`, {}, { headers: { Authorization: `Bearer ${getToken()}` } }); showMessage('success', 'Approved'); fetchRecords(); fetchStats() }
    catch (err) { showMessage('error', 'Failed to approve') }
  }

  const handleCancel = async (id) => {
    try { await axios.put(`${API_URL}/offduty/${id}/cancel`, {}, { headers: { Authorization: `Bearer ${getToken()}` } }); showMessage('success', 'Cancelled'); fetchRecords(); fetchStats() }
    catch (err) { showMessage('error', 'Failed to cancel') }
  }

  const handleDelete = async (id) => {
    if (!confirm('Delete this off day record?')) return
    try { await axios.delete(`${API_URL}/offduty/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } }); showMessage('success', 'Deleted'); fetchRecords(); fetchStats() }
    catch (err) { showMessage('error', 'Failed to delete') }
  }

  const openModal = (record = null) => {
    if (record) {
      setSelectedRecord(record)
      setFormData({
        guardId: record.guardId?._id || record.guardId || '',
        guardName: record.guardName || (record.guardId?.firstName ? `${record.guardId.firstName} ${record.guardId.lastName}` : ''),
        offDayType: record.offDayType || 'Rest Day',
        offDate: record.offDate ? new Date(record.offDate).toISOString().split('T')[0] : '',
        endDate: record.endDate ? new Date(record.endDate).toISOString().split('T')[0] : '',
        reason: record.reason || '', contactNumber: record.contactNumber || '',
        emergencyContact: record.emergencyContact || '', site: record.site || '',
        replacementGuardId: record.replacementGuardId?._id || record.replacementGuardId || ''
      })
    } else { setSelectedRecord(null); resetForm() }
    setShowModal(true)
  }

  const resetForm = () => {
    setFormData({ guardId: '', guardName: '', offDayType: 'Rest Day', offDate: '', endDate: '',
      reason: '', contactNumber: '', emergencyContact: '', site: '', replacementGuardId: '' })
  }

  const filteredRecords = records.filter(r => {
    const search = searchTerm.toLowerCase()
    const matchesSearch = !search || JSON.stringify(r).toLowerCase().includes(search)
    const matchesStatus = statusFilter ? (r.status || '') === statusFilter : true
    const matchesType = typeFilter ? (r.offDayType || '') === typeFilter : true
    return matchesSearch && matchesStatus && matchesType
  })

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A'
    try { const d = new Date(dateStr); if (isNaN(d.getTime())) return 'Invalid'; return d.toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' }) }
    catch { return 'Invalid' }
  }

  const getGuardStats = () => {
    const guardMap = {}
    records.forEach(r => {
      const gid = r.guardId?._id || r.guardId
      if (!gid) return
      if (!guardMap[gid]) guardMap[gid] = { guardId: gid, guardName: r.guardName || 'Unknown', guardCode: r.guardId?.guardId || '', usedDays: 0, scheduledDays: 0, activeDays: 0, remainingDays: 21 }
      const status = r.status || 'Scheduled'
      const days = r.daysOff || r.days || 1
      if (status === 'Completed') guardMap[gid].usedDays += days
      else if (status === 'Scheduled' || status === 'Approved') guardMap[gid].scheduledDays += days
      else if (status === 'Active') guardMap[gid].activeDays += days
    })
    Object.values(guardMap).forEach(g => { g.remainingDays = Math.max(0, 21 - g.usedDays) })
    return Object.values(guardMap).sort((a, b) => a.guardName.localeCompare(b.guardName))
  }

  const guardStats = getGuardStats()

  return (
    <div className="off-days-module">
      <div className="od-header">
        <div>
          <h1><Calendar size={28} /> Guard Off Days</h1>
          <p>Smart off days management with auto-calculated used, scheduled & remaining days</p>
        </div>
        <button className="od-btn-primary" onClick={() => openModal()}><Plus size={18} /> Schedule Off Day</button>
      </div>

      {message.text && (
        <div className={`od-alert od-alert-${message.type}`}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}{message.text}
        </div>
      )}

      <div className="od-filters">
        <div className="od-search-box"><Search size={16} /><input type="text" placeholder="Search guards, sites, types..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
        <div className="od-filter-group">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option><option value="Scheduled">Scheduled</option><option value="Active">Active</option>
            <option value="Completed">Completed</option><option value="Cancelled">Cancelled</option><option value="Pending Approval">Pending Approval</option>
          </select>
        </div>
        <div className="od-filter-group">
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">All Types</option>
            {offDayTypes.map(t => (<option key={t.id} value={t.id}>{t.name}</option>))}
          </select>
        </div>
      </div>

      <div className="od-stats">
        <div className="od-stat-card" style={{ borderLeftColor: '#ff9800' }}><TrendingUp size={20} color="#ff9800" /><span className="od-stat-number">{stats.scheduled}</span><span className="od-stat-label">Scheduled</span></div>
        <div className="od-stat-card" style={{ borderLeftColor: '#4caf50' }}><Clock size={20} color="#4caf50" /><span className="od-stat-number">{stats.active}</span><span className="od-stat-label">Active Now</span></div>
        <div className="od-stat-card" style={{ borderLeftColor: '#2196f3' }}><Award size={20} color="#2196f3" /><span className="od-stat-number">{stats.completed}</span><span className="od-stat-label">Completed</span></div>
        <div className="od-stat-card" style={{ borderLeftColor: '#9c27b0' }}><AlertCircle size={20} color="#9c27b0" /><span className="od-stat-number">{stats.pending}</span><span className="od-stat-label">Pending</span></div>
        <div className="od-stat-card" style={{ borderLeftColor: '#f44336' }}><X size={20} color="#f44336" /><span className="od-stat-number">{stats.cancelled}</span><span className="od-stat-label">Cancelled</span></div>
      </div>
     
      <div className="od-remaining-section">
        <h3><User size={18} /> Remaining Off Days by Guard</h3>
        <div className="od-remaining-grid">
          {guardStats.length > 0 ? guardStats.map(g => (
            <div key={g.guardId} className="od-remaining-card">
              <div className="od-remaining-guard"><strong>{g.guardName}</strong><span>{g.guardCode}</span></div>
              <div className="od-remaining-stats">
                <div className="od-remaining-item"><span className="od-remaining-used">{g.usedDays}</span><label>Used</label></div>
                <div className="od-remaining-item"><span className="od-remaining-scheduled">{g.scheduledDays + g.activeDays}</span><label>Scheduled</label></div>
                <div className="od-remaining-item"><span className="od-remaining-left" style={{color: g.remainingDays <= 3 ? '#f44336' : '#4caf50'}}>{g.remainingDays}</span><label>Remaining</label></div>
              </div>
            </div>
          )) : (<div className="od-remaining-empty">No guards with off day records</div>)}
        </div>
      </div>

      {loading ? (<div className="od-loading">Loading...</div>) : filteredRecords.length === 0 ? (
        <div className="od-empty"><Calendar size={48} opacity={0.3} /><p>No off day records found</p></div>
      ) : (
        <div className="od-table-container">
          <table className="od-table">
            <thead><tr><th>Guard</th><th>Type</th><th>Off Date</th><th>End Date</th><th>Days</th><th>Used</th><th>Remaining</th><th>Site</th><th>Replacement</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {filteredRecords.map(record => (
                <tr key={record._id}>
                  <td><div className="od-guard-info"><strong>{record.guardName || (record.guardId?.firstName ? `${record.guardId.firstName} ${record.guardId.lastName}` : 'Unknown Guard')}</strong><span>{record.guardId?.guardId || record.guardId?._id || ''}</span></div></td>
                  <td><span className="od-type-badge" style={{ backgroundColor: (offDayTypes.find(t => t.id === record.offDayType)?.color || '#666') + '20', color: offDayTypes.find(t => t.id === record.offDayType)?.color || '#666' }}>{record.offDayType || 'Rest Day'}</span></td>
                  <td>{formatDate(record.offDate)}</td><td>{formatDate(record.endDate)}</td>
                  <td><strong>{record.daysOff || record.days || 1}</strong> day(s)</td>
                  <td>{record.usedDays || 0}</td>
                  <td><span style={{color: (record.remainingDays || 21) <= 3 ? '#f44336' : '#4caf50', fontWeight: 'bold'}}>{record.remainingDays || Math.max(0, 21 - (record.usedDays || 0))}</span></td>
                  <td>{record.site || 'N/A'}</td>
                  <td>
                    {record.replacementGuardId === 'holiday' ? (<span className="od-replacement" style={{color: '#ff9800'}}>Holiday (All Off)</span>) :
                     record.replacementGuardName ? (<span className="od-replacement"><Shield size={12} /> {record.replacementGuardName}</span>) :
                     record.replacementGuardId && typeof record.replacementGuardId === 'object' ? (<span className="od-replacement"><Shield size={12} /> {record.replacementGuardId.firstName} {record.replacementGuardId.lastName}</span>) :
                     (<span className="od-no-replacement">None</span>)}
                  </td>
                  <td><span className="od-status-badge" style={{ backgroundColor: (statusColors[record.status] || '#666') + '20', color: statusColors[record.status] || '#666' }}>{record.status || 'Scheduled'}</span></td>
                  <td>
                    <div className="od-actions">
                      <button className="od-action-btn od-view" onClick={() => openModal(record)} title="View/Edit"><Edit size={14} /></button>
                      {record.status === 'Pending Approval' && (
                        <button className="od-action-btn od-approve" onClick={() => handleApprove(record._id)} title="Approve"><CheckCircle size={14} /></button>
                      )}
                      {(record.status === 'Scheduled' || record.status === 'Active' || record.status === 'Pending Approval') && (
                        <button className="od-action-btn od-cancel" onClick={() => handleCancel(record._id)} title="Cancel"><X size={14} /></button>
                      )}
                      <button className="od-action-btn od-delete" onClick={() => handleDelete(record._id)} title="Delete"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="od-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="od-modal">
            <div className="od-modal-header"><h3>{selectedRecord ? 'Edit Off Day' : 'Schedule Off Day'}</h3><button onClick={() => setShowModal(false)}><X size={20} /></button></div>
            <form onSubmit={handleSubmit} className="od-form">
              <div className="od-form-row">
                <div className="od-form-group">
                  <label>Guard *</label>
                  <select value={formData.guardId} onChange={(e) => { const g = guards.find(x => x._id === e.target.value); setFormData({...formData, guardId: e.target.value, guardName: g ? `${g.firstName} ${g.lastName}` : ''}) }} required>
                    <option value="">Select Guard</option>
                    {guards.map(g => (<option key={g._id} value={g._id}>{g.firstName} {g.lastName} ({g.guardId})</option>))}
                  </select>
                </div>
                <div className="od-form-group">
                  <label>Off Day Type *</label>
                  <select value={formData.offDayType} onChange={(e) => setFormData({...formData, offDayType: e.target.value})} required>
                    {offDayTypes.map(t => (<option key={t.id} value={t.id}>{t.name}</option>))}
                  </select>
                </div>
              </div>
              <div className="od-form-row">
                <div className="od-form-group"><label>Off Date *</label><input type="date" value={formData.offDate} onChange={(e) => setFormData({...formData, offDate: e.target.value})} required /></div>
                <div className="od-form-group"><label>End Date (optional)</label><input type="date" value={formData.endDate} onChange={(e) => setFormData({...formData, endDate: e.target.value})} /></div>
              </div>
              <div className="od-form-row">
                <div className="od-form-group"><label>Site</label><input type="text" value={formData.site} onChange={(e) => setFormData({...formData, site: e.target.value})} placeholder="Site name" /></div>
                <div className="od-form-group">
                  <label>Replacement Guard</label>
                  <select value={formData.replacementGuardId} onChange={(e) => setFormData({...formData, replacementGuardId: e.target.value})}>
                    <option value="">No Replacement</option><option value="holiday">Holiday (No Replacement - All Off)</option>
                    {guards.filter(g => g._id !== formData.guardId).map(g => (<option key={g._id} value={g._id}>{g.firstName} {g.lastName}</option>))}
                  </select>
                </div>
              </div>
              <div className="od-form-group od-form-full"><label>Reason</label><textarea value={formData.reason} onChange={(e) => setFormData({...formData, reason: e.target.value})} placeholder="Reason for off day..." rows={3} /></div>
              <div className="od-form-row">
                <div className="od-form-group"><label>Contact Number</label><input type="tel" value={formData.contactNumber} onChange={(e) => setFormData({...formData, contactNumber: e.target.value})} placeholder="Guard contact" /></div>
                <div className="od-form-group"><label>Emergency Contact</label><input type="tel" value={formData.emergencyContact} onChange={(e) => setFormData({...formData, emergencyContact: e.target.value})} placeholder="Emergency contact" /></div>
              </div>
              <div className="od-modal-footer">
                <button type="button" className="od-btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="od-btn-primary">{selectedRecord ? 'Update' : 'Schedule'} Off Day</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default OffDays
